import React, { useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { RootState, useAppThunkDispatch } from "redux/store";
import { createMarket } from "redux/slice/markets";
import { DashboardLayout } from "component/layout/dashboard";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { styles } from "./style";
import { DataInput } from "./components/data-input";
import { TranslationStatus } from "./components/translation-status";
import { TranslatedPreview } from "./components/translated-preview";
import { ParseResult, parseJsonData, validateDataFormat } from "./utils/parsers";
import { translateToMarket, TranslationResult } from "./utils/translators";
import { NewMarketResource } from "api/resource/market";
import * as env from "shared/env";

// Migration state interface
interface MigrationState {
	inputData: string;
	parseResults: ParseResult | null;
	isProcessing: boolean;
	errors: string[];
	marketData: any[];
	validationResults: string[];
	translationResults: TranslationResult<NewMarketResource> | null;
	isImporting: boolean;
	importComplete: boolean;
	selectedRecords: Set<number>;
	importedCount: number;
}

interface Props extends WithStyles<typeof styles> {
	reduxState: RootState;
}

const Component: React.FC<Props> = ({ classes = {}, reduxState }) => {
	// State management
	const dispatch = useAppThunkDispatch();
	const [migrationState, setMigrationState] = useState<MigrationState>({
		inputData: '',
		parseResults: null,
		isProcessing: false,
		errors: [],
		marketData: [],
		validationResults: [],
		translationResults: null,
		isImporting: false,
		importComplete: false,
		selectedRecords: new Set(),
		importedCount: 0
	});

	// Process data when input changes
	const processData = useCallback(async (data: string) => {
		if (!data.trim()) {
			setMigrationState(prev => ({
				...prev,
				inputData: data,
				parseResults: null,
				errors: [],
				marketData: [],
				validationResults: [],
				translationResults: null,
				importComplete: false,
				selectedRecords: new Set()
			}));
			return;
		}

		setMigrationState(prev => ({ ...prev, isProcessing: true }));

		try {
			// Parse JSON data
			const parseResults = parseJsonData(data);

			// Validate Market data
			const validationErrors = parseResults.data.length > 0 ? validateDataFormat(parseResults.data, 'market') : [];

			setMigrationState(prev => ({
				...prev,
				inputData: data,
				parseResults,
				isProcessing: false,
				errors: [...parseResults.errors, ...validationErrors],
				marketData: parseResults.data,
				validationResults: validationErrors,
				translationResults: null, // Reset translation results when new data is processed
				importComplete: false,
				selectedRecords: new Set()
			}));

		} catch(error) {
			setMigrationState(prev => ({
				...prev,
				inputData: data,
				parseResults: null,
				isProcessing: false,
				errors: [`Processing error: ${error instanceof Error ? error.message : 'Unknown error'}`],
				marketData: [],
				validationResults: [],
				translationResults: null,
				importComplete: false,
				selectedRecords: new Set()
			}));
		}
	}, [reduxState]);

	// Translate Market data manually
	const translateData = useCallback(async () => {
		if (!migrationState.parseResults || migrationState.validationResults.length > 0) {
			return;
		}

		setMigrationState(prev => ({ ...prev, isProcessing: true }));

		try {
			const translationResults = translateToMarket(migrationState.marketData, reduxState);
			setMigrationState(prev => ({
				...prev,
				translationResults,
				isProcessing: false,
				importComplete: false,
				selectedRecords: new Set(translationResults.translated.map((_, index) => index))
			}));
		} catch(translationError) {
			setMigrationState(prev => ({
				...prev,
				translationResults: null,
				isProcessing: false,
				errors: [...prev.errors, `Translation error: ${translationError instanceof Error ? translationError.message : 'Unknown translation error'}`],
				importComplete: false,
				selectedRecords: new Set()
			}));
		}
	}, [migrationState.parseResults, migrationState.validationResults, migrationState.marketData, reduxState]);

	// Handle data input changes
	const handleDataChange = useCallback((data: string) => {
		setTimeout(() => {
			processData(data);
		}, 500);
		setMigrationState(prev => ({
			...prev,
			inputData: data,
			importComplete: false,
			selectedRecords: new Set()
		}));
	}, [processData]);


	// Computed values
	const hasValidData = useMemo(() => {
		return migrationState.parseResults && 
			   migrationState.parseResults.validRecords > 0 && 
			   migrationState.validationResults.length === 0;
	}, [migrationState.parseResults, migrationState.validationResults]);

	const canTranslate = useMemo(() => {
		return hasValidData && !migrationState.translationResults;
	}, [hasValidData, migrationState.translationResults]);

	const hasTranslatedData = useMemo(() => {
		return migrationState.translationResults && migrationState.translationResults.translated.length > 0;
	}, [migrationState.translationResults]);

	// Handle record selection
	const handleSelectAll = useCallback((checked: boolean) => {
		if (checked && migrationState.translationResults) {
			const allIndices = new Set(migrationState.translationResults.translated.map((_, index) => index));
			setMigrationState(prev => ({ ...prev, selectedRecords: allIndices }));
		} else {
			setMigrationState(prev => ({ ...prev, selectedRecords: new Set() }));
		}
	}, [migrationState.translationResults]);

	const handleSelectRecord = useCallback((index: number, checked: boolean) => {
		setMigrationState(prev => {
			const newSelected = new Set(prev.selectedRecords);
			if (checked) {
				newSelected.add(index);
			} else {
				newSelected.delete(index);
			}
			return { ...prev, selectedRecords: newSelected };
		});
	}, []);

	// Handle import of translated markets
	const handleImportMarkets = useCallback(async () => {
		if (!migrationState.translationResults?.translated.length || migrationState.selectedRecords.size === 0) {
			return;
		}

		setMigrationState(prev => ({ ...prev, isImporting: true, importComplete: false }));

		try {
			const allMarkets = migrationState.translationResults.translated;
			// Only import selected markets
			const selectedMarkets = Array.from(migrationState.selectedRecords).map(index => allMarkets[index]);
			
			// Import each selected market using Redux action
			for (const marketResource of selectedMarkets) {
				// Convert NewMarketResource to NewMarket format
				const market = {
					...marketResource,
					criteria: marketResource.criteria || []
				};
				await dispatch(createMarket({ market }));
			}

			setMigrationState(prev => ({ 
				...prev, 
				isImporting: false, 
				importComplete: true,
				importedCount: selectedMarkets.length
			}));
		} catch(error) {
			setMigrationState(prev => ({ 
				...prev, 
				isImporting: false,
				errors: [...prev.errors, `Import error: ${error instanceof Error ? error.message : 'Unknown import error'}`]
			}));
		}
	}, [migrationState.translationResults, migrationState.selectedRecords, dispatch]);


	return (
		<DashboardLayout
			permitted={true}
			title="Migration Utility"
		>
			<Mui.Container maxWidth="xl">
				<Mui.Grid container spacing={3}>
					<Mui.Grid item xs={12}>
						<Mui.Box className={classes.header}>
							<Mui.Typography variant="h4" className={classes.title}>
								<FeatherIcon>
									<Icons.Database />
								</FeatherIcon>
								Market Migration Utility - Data Input & Processing
							</Mui.Typography>
							<Mui.Typography variant="body2" color="textSecondary">
								Import Market configurations with search criteria and URL parameters
							</Mui.Typography>
						</Mui.Box>
					</Mui.Grid>

					{/* Data Input - Left Column */}
					<Mui.Grid item xs={12} lg={12} style={{ height: 600 }}>
						<DataInput
							onDataChange={handleDataChange}
							isProcessing={migrationState.isProcessing}
							errors={migrationState.parseResults?.errors || []}
						/>
					</Mui.Grid>

					{/* Translation Status - Full Width */}
					<Mui.Grid item xs={12}>
						<TranslationStatus
							isProcessing={migrationState.isProcessing}
							parseResults={migrationState.parseResults}
							validationErrors={migrationState.validationResults}
						/>
					</Mui.Grid>

					{/* Translate Button */}
					{hasValidData && (
						<Mui.Grid item xs={12}>
							<Mui.Box display="flex" justifyContent="center" py={2}>
								<Mui.Button
									variant="contained"
									color="primary"
									size="large"
									onClick={translateData}
									disabled={!canTranslate || migrationState.isProcessing}
									startIcon={<FeatherIcon><Icons.ArrowRight /></FeatherIcon>}
									style={{ minWidth: 200, height: 48 }}
								>
									{migrationState.translationResults ? 'Retranslate Data' : 'Translate Market Data'}
								</Mui.Button>
							</Mui.Box>
						</Mui.Grid>
					)}

					{/* Import Complete Message */}
					{migrationState.importComplete && (
						<Mui.Grid item xs={12}>
							<Mui.Box display="flex" justifyContent="center" py={2}>
								<Mui.Paper 
									elevation={3}
									style={{ 
										minWidth: 300, 
										fontSize: '1.1rem',
										backgroundColor: '#4caf50',
										color: 'white',
										padding: '16px',
										borderRadius: '4px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center'
									}}
								>
									✅ Import Complete! {migrationState.importedCount} market{migrationState.importedCount !== 1 ? 's' : ''} successfully imported.
								</Mui.Paper>
							</Mui.Box>
						</Mui.Grid>
					)}

					{/* Translated Records Preview */}
					<Mui.Grid item xs={12}>
						<TranslatedPreview
							translatedRecords={migrationState.translationResults?.translated || []}
							isLoading={migrationState.isProcessing}
							errors={migrationState.translationResults?.errors || []}
							warnings={migrationState.translationResults?.warnings || []}
							selectedRecords={migrationState.selectedRecords}
							onSelectAll={handleSelectAll}
							onSelectRecord={handleSelectRecord}
						/>
					</Mui.Grid>

					{/* Import Selected Records Button */}
					{hasTranslatedData && (
						<Mui.Grid item xs={12}>
							<Mui.Box display="flex" justifyContent="center" py={3}>
								<Mui.Button
									variant="contained"
									color={"primary"}
									size="large"
									startIcon={<FeatherIcon><Icons.Download /></FeatherIcon>}
									style={{ 
										minWidth: 250, 
										height: 56,
										fontSize: '1.1rem',
										fontWeight: 600
									}}
									onClick={handleImportMarkets}
									disabled={migrationState.isImporting || migrationState.selectedRecords.size === 0}
								>
									{migrationState.isImporting ? 'Importing...' : `Import Selected Records (${migrationState.selectedRecords.size})`}
								</Mui.Button>
							</Mui.Box>
						</Mui.Grid>
					)}
				</Mui.Grid>
			</Mui.Container>
		</DashboardLayout>
	);
};

const mapStateToProps = (state: RootState) => ({
	reduxState: state
});

export const MigrationUtilityPage = withStyles(connect(mapStateToProps)(Component), styles);